import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import SelectAcreedorModal from "../SelectAcreedorModal";

import ApoderadosApi from "api/apoderadosApi";
import DatosBasicosApi from "api/datosbasicosApi";

const INITIAL_FORM_STATE = {
  acreedor: null,
  apoderado: null,
  clase_deuda: "",
  capital: "",
  capitalFormatted: "",
  reconoce_intereses: false,
  suma_intereses: false,
  intereses: "",
  interesesFormatted: "",
  naturaleza: "",
  //dias_mora: 0,
  rango_mora: "",
  tipo_garantia: "",
  fecha_otorgamiento: "",
  fecha_vencimiento: "",
  garantia: "",
  correo_pagador: "",
  postergado: false,
  codeudor_avalista: false,
  costas: false,
  // Nuevos campos para pagador_libranza
  pagador_libranza: {
    nombre_completo: "",
    email: "",
    tipo_identificacion: "",
    identificacion: "",
    direccion: "",
    celular: "",
  },
};

function NuevaDeudaForm({ open, onClose, onSubmit, procesoId }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [showAcreedorModal, setShowAcreedorModal] = useState(false);
  const [clasesDeuda, setClasesDeuda] = useState([]);
  const [apoderados, setApoderados] = useState([]);

  useEffect(() => {
    if (open) {
      setFormData(INITIAL_FORM_STATE);
      fetchClasesDeuda();
    }
  }, [open]);

  const fetchClasesDeuda = async () => {
    try {
      const response = await DatosBasicosApi.fetchClasesDeuda();
      const clasesOrdenadas = response.data.sort((a, b) => a.id - b.id);
      setClasesDeuda(clasesOrdenadas);
    } catch (error) {
      console.error("Error al cargar clases de deuda:", error);
    }
  };

  const obtenerApoderados = async (acreedorId) => {
    if (!acreedorId) return; // Evita llamadas innecesarias
    try {
      const response = await ApoderadosApi.fetchApoderadosAcreedor(acreedorId);
      if (response.data && !response.data.message) {
        setApoderados(response.data || []);
      } else {
        setApoderados(null);
      }
    } catch (error) {
      console.error("Error al obtener los apoderados del acreedor:", error);
      setApoderados(null);
    }
  };

  const handleAcreedorSelect = (selectedAcreedor) => {
    setFormData((prev) => ({
      ...prev,
      acreedor: selectedAcreedor,
      apoderado: null,
    }));
    if (selectedAcreedor?.id) {
      obtenerApoderados(selectedAcreedor.id);
    }
    setShowAcreedorModal(false);
  };

  const formatCurrency = (value) => {
    const number = value.replace(/\D/g, "");
    const numberValue = Number(number);
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(number || 0).replace(/\s/g, ""),
      value: numberValue,
    };
  };

  const handleDateChange = (field) => (newValue) => {
    if (newValue) {
      const year = newValue.$y;
      const month = String(newValue.$M + 1).padStart(2, "0");
      const day = String(newValue.$D).padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;
      setFormData({
        ...formData,
        [field]: fecha,
      });
    } else {
      setFormData({
        ...formData,
        [field]: null,
      });
    }
  };

  const handleMoneyChange = (field) => (event) => {
    const { formatted, value } = formatCurrency(event.target.value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Formatted`]: formatted,
    }));
  };

  const handleChange = (field) => (event) => {
    let value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    if (field === "clase_deuda") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        naturaleza: formData.naturaleza,
        tipo_garantia: "",
        correo_pagador: "",
        postergado: false, // Resetear postergado al cambiar de clase
      }));
    } else if (field === "naturaleza") {
      value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Nuevo manejador para campos del pagador de libranza
  const handlePagadorLibranzaChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      pagador_libranza: {
        ...prev.pagador_libranza,
        [field]: value,
      },
    }));
  };

  // Verificar si la naturaleza contiene "Libranza" (case insensitive)
  const isLibranza = formData.naturaleza.toLowerCase().includes("libranza");

  const handleSubmit = () => {
    if (!procesoId || !formData.acreedor || !formData.clase_deuda || !formData.capital) {
      return;
    }
    const claseDeuda = clasesDeuda.find((c) => c.id === Number(formData.clase_deuda));
    if (!claseDeuda) {
      console.error("Clase de deuda no encontrada.");
      return;
    }

    // Preparar pagador_libranza solo si es libranza y tiene datos
    let pagadorLibranzaData = null;
    if (isLibranza) {
      const { pagador_libranza } = formData;
      // Solo incluir pagador_libranza si al menos tiene nombre_completo
      if (pagador_libranza.nombre_completo.trim()) {
        pagadorLibranzaData = {
          nombre_completo: pagador_libranza.nombre_completo.trim(),
          email: pagador_libranza.email.trim(),
          tipo_identificacion: pagador_libranza.tipo_identificacion.trim(),
          identificacion: pagador_libranza.identificacion.trim(),
          direccion: pagador_libranza.direccion.trim(),
          celular: pagador_libranza.celular.trim(),
        };
      }
    }

    const deudaData = {
      proceso: procesoId,
      acreedor: formData.acreedor.id,
      apoderado: formData.apoderado?.id,
      clase_deuda: formData.clase_deuda,
      capital: formData.capital.toString(),
      reconoce_intereses: formData.reconoce_intereses,
      suma_intereses: formData.suma_intereses,
      intereses: formData.intereses,
      naturaleza: formData.naturaleza,
      //dias_mora: formData.dias_mora,
      rango_mora: formData.rango_mora,
      postergado: formData.postergado,
      costas: formData.costas,
      codeudor_avalista: formData.codeudor_avalista,
      tipo_garantia: formData.tipo_garantia,
      fecha_otorgamiento: formData.fecha_otorgamiento || null,
      fecha_vencimiento: formData.fecha_vencimiento || null,
      garantia: formData.garantia,
      pagador_libranza: pagadorLibranzaData, // Nuevo campo
      str_acreedor: {
        id: formData.acreedor.id,
        tipo_identificacion: formData.acreedor.tipo_identificacion,
        identificacion: formData.acreedor.identificacion,
        nombre: formData.acreedor.nombre,
        apellido: formData.acreedor.apellido,
        direccion: formData.acreedor.direccion,
        correos: formData.acreedor.correos || [],
      },
      str_clase_deuda: {
        id: claseDeuda.id,
        nombre: claseDeuda.nombre,
        descripcion: claseDeuda.descripcion,
      },
    };
    onSubmit(deudaData);
    setFormData(INITIAL_FORM_STATE);
    onClose();
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM_STATE);
    onClose();
  };

  const claseActual = formData.clase_deuda
    ? clasesDeuda.find((c) => c.id === Number(formData.clase_deuda))
    : null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            padding: "16px 24px",
            fontSize: "1.25rem", // Equivalente a h5
            fontWeight: 400,
          }}
        >
          Nueva Acreencia
        </DialogTitle>
        <DialogContent>
          <SuiBox py={3}>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Acreedor
                  </SuiTypography>
                  <SuiButton
                    variant="gradient"
                    buttonColor="info"
                    fullWidth
                    onClick={() => setShowAcreedorModal(true)}
                  >
                    {formData.acreedor
                      ? `${formData.acreedor.nombre} ${formData.acreedor.apellido || ""}`
                      : "Seleccionar Acreedor"}
                  </SuiButton>
                </SuiBox>
              </Grid>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Apoderado Acreedor para esta Deuda
                  </SuiTypography>
                  <Autocomplete
                    disablePortal
                    options={apoderados || []}
                    getOptionLabel={(option) => option?.nombre_completo || ""}
                    sx={{ mb: 3 }}
                    renderInput={(params) => <TextField {...params} />}
                    value={formData.apoderado || null}
                    onChange={(event, value) => {
                      setFormData((prev) => ({
                        ...prev,
                        apoderado: value || null,
                      }));
                    }}
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Clase Deuda
                  </SuiTypography>
                  <FormControl fullWidth>
                    <Select value={formData.clase_deuda} onChange={handleChange("clase_deuda")}>
                      <MenuItem value="">
                        <em>Seleccione una Clase de Deuda</em>
                      </MenuItem>
                      {clasesDeuda.map((clase) => (
                        <MenuItem key={clase.id} value={clase.id}>
                          {clase.nombre} - {clase.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Tipo Garantía
                  </SuiTypography>
                  {!claseActual?.tipos_garantia && (
                    <SuiInput
                      value={formData.tipo_garantia}
                      onChange={handleChange("tipo_garantia")}
                      placeholder="Tipo Garantía"
                      fullWidth
                    />
                  )}
                  {claseActual?.tipos_garantia?.length > 0 && (
                    <Select value={formData.tipo_garantia} onChange={handleChange("tipo_garantia")}>
                      <MenuItem value="">Seleccione un Tipo Garantía</MenuItem>
                      <MenuItem value="Costas Procesales">Costas Procesales</MenuItem>
                      {claseActual.tipos_garantia.map((gar) => (
                        <MenuItem key={gar} value={gar}>
                          {gar.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </SuiBox>
              </Grid>
              {formData.tipo_garantia === "Otro" && (
                <Grid size={6}>
                  <SuiBox mb={2}>
                    <SuiTypography variant="h6" fontWeight="bold">
                      Garantía
                    </SuiTypography>
                    <SuiInput
                      type="garantia"
                      value={formData.garantia}
                      onChange={handleChange("garantia")}
                      placeholder="Garantía"
                      fullWidth
                    />
                  </SuiBox>
                </Grid>
              )}
              {formData.tipo_garantia === "Libranza" && (
                <Grid size={6}>
                  <SuiBox mb={2}>
                    <SuiTypography variant="h6" fontWeight="bold">
                      Correo Pagador
                    </SuiTypography>
                    <SuiInput
                      type="email"
                      value={formData.correo_pagador}
                      onChange={handleChange("correo_pagador")}
                      placeholder="Correo del Pagador"
                      fullWidth
                    />
                  </SuiBox>
                </Grid>
              )}
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Monto Deuda
                  </SuiTypography>
                  <SuiInput
                    value={formData.capitalFormatted}
                    onChange={handleMoneyChange("capital")}
                    placeholder="Monto de la Deuda"
                    fullWidth
                  />
                </SuiBox>
              </Grid>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Días Mora
                  </SuiTypography>
                  <Select
                    name="rango_mora"
                    value={formData.rango_mora}
                    onChange={handleChange("rango_mora")}
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="AL DIA">AL DIA</MenuItem>
                    <MenuItem value="MENOS DE 90 DIAS">MENOS DE 90 DIAS</MenuItem>
                    <MenuItem value="MAS DE 90 DIAS">MAS DE 90 DIAS</MenuItem>
                  </Select>
                  {/* <SuiInput
                    type="number"
                    value={formData.dias_mora}
                    onChange={handleChange("dias_mora")}
                    placeholder="Días en Mora"
                    fullWidth
                  /> */}
                </SuiBox>
              </Grid>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    Naturaleza
                  </SuiTypography>
                  <SuiInput
                    value={formData.naturaleza}
                    onChange={handleChange("naturaleza")}
                    placeholder="Naturaleza"
                    fullWidth
                  />
                </SuiBox>
              </Grid>
            </Grid>

            {/* Sección del Pagador de Libranza - Solo se muestra si naturaleza contiene "Libranza" */}
            {isLibranza && (
              <>
                <SuiBox>
                  <SuiTypography variant="h5" fontWeight="bold" color="info">
                    Información del Pagador de Libranza
                  </SuiTypography>
                </SuiBox>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.nombre_completo}
                        onChange={handlePagadorLibranzaChange("nombre_completo")}
                        placeholder="Nombre Completo del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        type="email"
                        value={formData.pagador_libranza.email}
                        onChange={handlePagadorLibranzaChange("email")}
                        placeholder="Email del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.celular}
                        onChange={handlePagadorLibranzaChange("celular")}
                        placeholder="Número de Celular del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <Select
                        value={formData.pagador_libranza.tipo_identificacion}
                        onChange={handlePagadorLibranzaChange("tipo_identificacion")}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">Seleccione Tipo Identificación</MenuItem>
                        <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                        <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                        <MenuItem value="NIT">NIT</MenuItem>
                        <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                      </Select>
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.identificacion}
                        onChange={handlePagadorLibranzaChange("identificacion")}
                        placeholder="Número de Identificación del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.direccion}
                        onChange={handlePagadorLibranzaChange("direccion")}
                        placeholder="Dirección del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                </Grid>
              </>
            )}

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    ¿Conoce Intereses Causados?{"  "}
                    <Checkbox
                      checked={formData.reconoce_intereses}
                      onChange={handleChange("reconoce_intereses")}
                    />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              {formData.reconoce_intereses && (
                <Grid size={4}>
                  <SuiBox mb={2}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.interesesFormatted}
                        onChange={handleMoneyChange("intereses")}
                        placeholder="Monto de los Intereses"
                        fullWidth
                      />
                    </SuiBox>
                  </SuiBox>
                </Grid>
              )}
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    ¿Es Codeudor o Avalista?{"  "}
                    <Checkbox
                      checked={formData.codeudor_avalista}
                      onChange={handleChange("codeudor_avalista")}
                    />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    ¿Acreedor Postergado?{"  "}
                    <Checkbox checked={formData.postergado} onChange={handleChange("postergado")} />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="bold">
                    ¿Costas?{"  "}
                    <Checkbox checked={formData.costas} onChange={handleChange("costas")} />
                  </SuiTypography>
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="bold">
                  Fecha Otorgamiento
                </SuiTypography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    clearable
                    name="fecha_otorgamiento"
                    value={formData.fecha_otorgamiento ? dayjs(formData.fecha_otorgamiento) : null}
                    onChange={handleDateChange("fecha_otorgamiento")}
                    format="DD/MM/YYYY"
                    renderInput={(params) => (
                      <SuiInput
                        {...params}
                        icon={{
                          component: <CalendarTodayIcon />,
                          direction: "right",
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="bold">
                  Fecha Vencimiento
                </SuiTypography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    clearable
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento ? dayjs(formData.fecha_vencimiento) : null}
                    onChange={handleDateChange("fecha_vencimiento")}
                    format="DD/MM/YYYY"
                    renderInput={(params) => (
                      <SuiInput
                        {...params}
                        icon={{
                          component: <CalendarTodayIcon />,
                          direction: "right",
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </SuiBox>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={handleClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton onClick={handleSubmit} variant="gradient" buttonColor="dark">
            Agregar Acreencia
          </SuiButton>
        </DialogActions>
      </Dialog>

      <SelectAcreedorModal
        open={showAcreedorModal}
        onClose={() => setShowAcreedorModal(false)}
        onSelect={handleAcreedorSelect}
      />
    </>
  );
}

NuevaDeudaForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  procesoId: PropTypes.number.isRequired,
};

export default NuevaDeudaForm;
